// ─────────────────────────────────────────────
//  staffAliases — the answers humans give, kept, so the question is asked once.
//
//  Anesthesia Team Control (ATC)
//  Copyright © 2026 Gonzalo Martinez (gamc00@gmail.com). All rights reserved.
//  Proprietary and confidential. Not licensed for public use — see /LICENSE.
// ─────────────────────────────────────────────
//
//  WHAT THIS COLLECTION IS FOR. The import ladder (domain/resolveIdentity)
//  refuses to guess: a name it cannot place becomes a review item, and a person
//  answers it. Without somewhere to PUT that answer the same twenty questions
//  arrive every morning, and the system hovers instead of converging. One
//  document = one raw sheet string that a named human bound to one staff record.
//
//  ⚠️ AN ALIAS IS A HUMAN'S ANSWER, NOT A GUESS THE IMPORT LEARNED. Nothing
//  unattended may write here. That is enforced in firestore.rules (admin/AIC
//  only) rather than by convention, because the failure it prevents is the
//  importer teaching itself that `to OB floor` is a colleague and then matching
//  it forever — a phantom that LOOKS resolved.
//
//  ⚠️ THE ALIAS IS FACILITY-SCOPED. Two campuses can hold two different
//  colleagues who are both written `J. Patel`, and this tenant runs one
//  Firestore project per hospital but three campuses inside it. An alias that
//  leaked across campuses would answer confidently for the wrong person, so
//  `facilityId` is part of the document id and is immutable in the rules.
//
//  ⚠️ THE DOC ID IS DERIVED FROM THE NORMALISED STRING, ON PURPOSE. Confirming
//  the same name twice must UPDATE one record, not create a second one that
//  disagrees with the first. `normalizeName` is the app's existing join key —
//  no second normaliser is defined here.
//
//  Pure — no Firebase, no React, no network. The firestore helpers that write
//  these documents live in src/firebase/firestore.js.
// ─────────────────────────────────────────────

import { normalizeName } from "../features/team-directory/normalizeName";
import { slugify } from "../features/team-directory/slug";

export const STAFF_ALIASES_COLLECTION = "staff_aliases";

// Where the raw string came from, so a reviewer can see what they are binding.
// `import-review` is the only one a human clicks today; the others exist so a
// future surface does not invent an unlabelled fourth.
export const ALIAS_SOURCES = ["import-review", "roster-review", "manual"];
const DEFAULT_SOURCE = "import-review";

/** The key an alias is looked up by — the app's one join key, not a new one. */
export const aliasKey = (raw) => normalizeName(raw);

/**
 * Stable document id: one alias per (facility, normalised name).
 *
 * ⚠️ The normalised key is slugified for the id but STORED verbatim in
 * `normalised`, because the id is only an addressing convenience and the query
 * is on the field. Two names that slugify the same but normalise differently
 * would otherwise silently share a record.
 */
export function aliasDocId(facilityId, raw) {
  const key = aliasKey(raw);
  if (!facilityId || !key) return null;
  return `${slugify(facilityId)}__${slugify(key)}`;
}

/**
 * Build the document a confirmation writes.
 *
 * @param {object} input
 *   @param {string} input.raw          verbatim string from the sheet cell
 *   @param {string} input.staffId      the record the human chose
 *   @param {string} input.facilityId   campus the sheet row belongs to
 *   @param {string} input.confirmedBy  uid of the human confirming
 *   @param {Date|number|object} [input.confirmedAt]
 *   @param {string} [input.source]
 * @returns {{ ok: boolean, reason: string, id: string|null, data: object|null }}
 *
 * Returns a REASON rather than throwing: this runs behind a click in a review
 * queue, and an exception there loses the reviewer's whole pass over the list.
 */
export function buildAliasRecord(input = {}) {
  const raw = String(input.raw ?? "").trim();
  const normalised = aliasKey(raw);
  const staffId = String(input.staffId ?? "").trim();
  const facilityId = String(input.facilityId ?? "").trim();
  const confirmedBy = String(input.confirmedBy ?? "").trim();

  if (!raw || !normalised) return fail("the raw name is empty once normalised");
  if (!staffId) return fail("no staff record was chosen");
  if (!facilityId) return fail("no facility");
  if (!confirmedBy) return fail("no confirming user — an alias must name a human");

  const source = ALIAS_SOURCES.includes(input.source) ? input.source : DEFAULT_SOURCE;
  return {
    ok: true,
    reason: "",
    id: aliasDocId(facilityId, raw),
    data: {
      raw,
      normalised,
      staffId,
      facilityId,
      confirmedBy,
      confirmedAt: input.confirmedAt ?? null,
      source,
    },
  };
}

const fail = (reason) => ({ ok: false, reason, id: null, data: null });

/**
 * Drop aliases that no longer point at a live staff record.
 *
 * A record can be deleted or merged after somebody bound a name to it; the
 * alias then resolves to a ghost id that every downstream screen renders as a
 * blank. Callers hand the survivors to `resolveIdentity` and the rest to the
 * review queue.
 *
 * @param {Array<object>} aliases
 * @param {Array<{id:string}>} staff
 * @returns {{ live: object[], stale: object[] }}
 */
export function partitionAliases(aliases, staff) {
  const ids = new Set((Array.isArray(staff) ? staff : []).map((s) => s?.id).filter(Boolean));
  const live = [];
  const stale = [];
  (Array.isArray(aliases) ? aliases : []).forEach((a) => {
    if (a && a.staffId && ids.has(a.staffId)) live.push(a);
    else if (a) stale.push(a);
  });
  return { live, stale };
}
